import React, { useEffect } from 'react';
import {
  Box,
  Grid,
  Modal,
  TextField,
  Select,
  MenuItem,
  Stack,
  Alert,
  Snackbar,
  FormControl,
} from '@mui/material';
import {
  AppButton,
  OutlineButton,
} from '../../../../resources/styling/appStyle';
import { colors } from '../../../../resources/theme/colors';
import {
  QUESTION_TYPE,
  QUESTION_TYPE_SELECT,
  COMMENT_OPTIONS,
  YES_NO_OPTIONS,
  YES_NO_MAYBE_OPTIONS,
  MULTI_OPTIONS,
  PERCENTAGE_SLIDER_OPTIONS,
  NUMBER_SLIDER_OPTIONS,
  FOLLOWUP_QUESTION_OPTIONS,
  SETTINGS_OPTIONS,
  QUESTION_CATEGORY,
} from '../../utils';
import { QUESTION_Library_TEMPLATE } from '../../utils';
import questionService from '../../../../services/api/insights/questionService';
import tagService from '../../../../services/api/insights/tagService';
import Slider from './Slider';
import MultiOption from './MultiOption';
import AudienceTags from './AudienceTags';

const { mandy, pictonBlue } = colors;

export default function AddEditQuestion({
  doneEdit,
  question,
  updateAfterEdit,
}) {
  const [showOutput, setShowOutput] = React.useState({
    open: false,
    message: '',
    type: 'success',
  });

  const [audiences, setAudiences] = React.useState([1]);
  const [selectedTab, setSelectedTab] = React.useState(0);
  const [tags, setTags] = React.useState([]);
  const [selectedTags, setSelectedTags] = React.useState([]);
  const [questionData, setQuestionData] = React.useState(
    question === null || question === undefined
      ? {
          ...QUESTION_Library_TEMPLATE,
          audiences: 1,
        }
      : {
          ...question,
          isAdmin: true,
        }
  );

  useEffect(() => {
    loadTags();
    loadAudiences();
  }, [question]);

  const loadTags = () =>
    tagService.getTags().then((res) => {
      setTags(res.sort((a, b) => a.name.localeCompare(b.name)));
      if (question) {
        let v = [];
        let qt = [];
        question.questionTag.map((tag) => {
          if (qt.findIndex((x) => x === tag.tagId) === -1) qt.push(tag.tagId);
        });
        res.filter((x) => {
          if (qt.findIndex((y) => y === x.id) !== -1) {
            v.push(x);
          }
        });

        setSelectedTags(v);
      }
    });

  const loadAudiences = () => {
    let a = audiences;
    if (question) {
      if (question.audiences === 1) {
        a = [1];
      }
      if (question.audiences === 2) {
        a = [2];
      }
      if (question.audiences === 3) {
        a = [1, 2];
      }
      if (question.audiences === 4) {
        a = [4];
      }
      if (question.audiences === 5) {
        a = [1, 4];
      }
      if (question.audiences === 6) {
        a = [2, 4];
      }
      if (question.audiences === 7) {
        a = [1, 2, 4];
      }
    } else {
      a = [1];
    }
    setAudiences(a);
    setSelectedTab(a[0]);
  };

  const handleQuestionTypeChange = (e) => {
    let openOption = questionData.openResponseAttributes;
    let multiOption = questionData.multiOptionResponseAttributes
      ? [...questionData.multiOptionResponseAttributes]
      : [];
    let sliderOption = questionData.sliderResponseAttributes;
    let questionSetting = questionData.questionSettings;
    let followupQuestion = questionData.followupQuestions
      ? [...questionData.followupQuestions]
      : [];

    // open
    if (e.target.value === QUESTION_TYPE.COMMENTS) {
      if (
        questionData.openResponseAttributes === null ||
        questionData.openResponseAttributes === undefined
      ) {
        openOption = { ...COMMENT_OPTIONS };
        questionSetting = { ...SETTINGS_OPTIONS };
        followupQuestion = [{ ...FOLLOWUP_QUESTION_OPTIONS }];
      }
    }
    if (
      e.target.value === QUESTION_TYPE.MULTIPLE_CHOICE ||
      e.target.value === QUESTION_TYPE.MULTIPLE_SELECT ||
      e.target.value === QUESTION_TYPE.LIKERT ||
      e.target.value === QUESTION_TYPE.YES_NO_MAYBE ||
      e.target.value === QUESTION_TYPE.YES_NO
    ) {
      multiOption =
        e.target.value === QUESTION_TYPE.YES_NO
          ? [...YES_NO_OPTIONS]
          : e.target.value === QUESTION_TYPE.YES_NO_MAYBE
          ? [...YES_NO_MAYBE_OPTIONS]
          : [...MULTI_OPTIONS];
      questionSetting = { ...SETTINGS_OPTIONS };
      followupQuestion = [{ ...FOLLOWUP_QUESTION_OPTIONS }];
    }

    if (
      e.target.value === QUESTION_TYPE.PERCENTAGE ||
      e.target.value === QUESTION_TYPE.NUMBER_SLIDER
    ) {
      sliderOption =
        e.target.value === QUESTION_TYPE.PERCENTAGE
          ? { ...PERCENTAGE_SLIDER_OPTIONS }
          : { ...NUMBER_SLIDER_OPTIONS };
      questionSetting = { ...SETTINGS_OPTIONS };
      followupQuestion = [{ ...FOLLOWUP_QUESTION_OPTIONS }];
    }

    setQuestionData({
      ...questionData,
      questionType: e.target.value,
      openResponseAttributes: { ...openOption },
      questionSettings: { ...questionSetting },
      followupQuestions: followupQuestion ? [...followupQuestion] : null,
      sliderResponseAttributes: { ...sliderOption },
      multiOptionResponseAttributes: multiOption ? [...multiOption] : null,
      isAdmin: true,
    });
  };

  return (
    <Modal open={true} onClose={() => doneEdit(false)}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          p: 3,
        }}
      >
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          open={showOutput.open}
          autoHideDuration={6000}
          onClose={() => setShowOutput({ ...showOutput, open: false })}
        >
          <Alert
            variant='filled'
            onClose={() => setShowOutput({ ...showOutput, open: false })}
            severity={showOutput.type}
            sx={{ width: '100%' }}
          >
            {showOutput.message}
          </Alert>
        </Snackbar>
        <Grid container rowSpacing={1.5}>
          <Grid item xs={12} lg={12} md={12} sm={12}>
            <h2>
              {questionData.id === 0 ? 'Add new ' : 'Edit '}Question
            </h2>
          </Grid>
          <Grid item xs={12} lg={12} md={12} sm={12}>
            <AudienceTags
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
              audiences={audiences}
              setAudiences={setAudiences}
            />
          </Grid>
          <Grid item xs={12} lg={12} md={12} sm={12}>
            <FormControl fullWidth>
              <div className='form-label'>Question</div>
              <TextField
                fullWidth
                placeholder='Enter your question'
                value={
                  selectedTab === 1
                    ? questionData?.staffText
                    : selectedTab === 2
                    ? questionData?.parentText
                    : questionData?.pupilText
                }
                onChange={(e) => {
                  let q = { ...questionData };
                  if (selectedTab === 1) {
                    q.staffText = e.target.value;
                  } else if (selectedTab === 2) {
                    q.parentText = e.target.value;
                  } else {
                    q.pupilText = e.target.value;
                  }
                  if (q.staffText === '') {
                    q.staffText = e.target.value;
                  }
                  if (q.parentText === '') {
                    q.parentText = e.target.value;
                  }
                  if (q.pupilText === '') {
                    q.pupilText = e.target.value;
                  }
                  setQuestionData({
                    ...q,
                  });
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} lg={12} md={12} sm={12}>
            <FormControl fullWidth>
              <div className='form-label'>Tags</div>
              <Select
                fullWidth
                multiple
                value={selectedTags}
                onChange={(e) => {
                  if (e.target.value.length <= 3) {
                    setSelectedTags(e.target.value);
                    setQuestionData({
                      ...questionData,
                      tags: e.target.value,
                    });
                  } else {
                    alert('You can only select 3 tags');
                  }
                }}
              >
                {tags?.map((tag, index) => (
                  <MenuItem key={index} value={tag}>
                    {tag.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} lg={12} md={12} sm={12}>
            <FormControl fullWidth>
              <div className='form-label'>Answer</div>
              <Select
                fullWidth
                value={questionData.questionType ?? ''}
                onChange={handleQuestionTypeChange}
                renderValue={(selected) => {
                  const item = QUESTION_TYPE_SELECT.find(
                    (x) => x.value === selected
                  );
                  return (
                    <Stack direction='row' spacing={2}>
                      <span>{item?.label}</span>
                    </Stack>
                  );
                }}
              >
                {QUESTION_TYPE_SELECT.map((questionType, index) => (
                  <MenuItem key={index} value={questionType.value}>
                    <Stack direction='row' spacing={2}>
                      {/* {questionType.image} */}
                      <span>{questionType.label}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} lg={12} md={12} sm={12}>
            <FormControl fullWidth>
              <div className='form-label'>Question Category</div>
              <Select
                fullWidth
                value={questionData.categoryId ?? ''}
                onChange={(e) => {
                  setQuestionData({
                    ...questionData,
                    categoryId: e.target.value,
                  });
                }}
                renderValue={(selected) => {
                  const item = QUESTION_CATEGORY.find(
                    (x) => x.value === selected
                  );
                  return (
                    <Stack direction='row' spacing={2}>
                      <span>{item?.label}</span>
                    </Stack>
                  );
                }}
              >
                {QUESTION_CATEGORY.map((category, index) => (
                  <MenuItem key={index} value={category.value}>
                    <Stack direction='row' spacing={2}>
                      {/* {questionType.image} */}
                      <span>{category.label}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Slider
            questionData={questionData}
            setQuestionData={setQuestionData}
            selectedTab={selectedTab}
          />
          <MultiOption
            questionData={questionData}
            setQuestionData={setQuestionData}
            selectedTab={selectedTab}
          />
          <Grid item xs={12} lg={12} md={12} sm={12}>
            <Stack direction='row' gap={1} justifyContent='flex-end'>
              <OutlineButton onClick={() => doneEdit(false)}>
                {questionData.id === 0 ? 'Cancel' : 'Close'}
              </OutlineButton>
              <AppButton
                color={mandy}
                onClick={() => {
                  const audienceTextErrors = audiences
                    .map((aud) => {
                      const field =
                        aud === 1
                          ? 'staffText'
                          : aud === 2
                          ? 'parentText'
                          : 'pupilText';
                      const label =
                        aud === 1 ? 'Staff' : aud === 2 ? 'Parent' : 'Pupil';
                      const value = questionData?.[field];
                      return value && value.trim() !== '' ? null : label;
                    })
                    .filter(Boolean);
                  if (audienceTextErrors.length > 0) {
                    setShowOutput({
                      open: true,
                      message: `Please enter question text for: ${audienceTextErrors.join(
                        ', '
                      )}`,
                      type: 'error',
                    });
                    return;
                  }
                  if (
                    !questionData.questionType ||
                    questionData.questionType === 0
                  ) {
                    setShowOutput({
                      open: true,
                      message: 'Please select an answer type',
                      type: 'error',
                    });
                    return;
                  }
                  if (!selectedTags || selectedTags.length === 0) {
                    setShowOutput({
                      open: true,
                      message: 'Please select at least one tag',
                      type: 'error',
                    });
                    return;
                  }
                  let data = {
                    ...questionData,
                    questionTag: selectedTags.map((tag) => {
                      return { tagId: tag.id };
                    }),
                    multiOptionResponseAttributes:
                      questionData.questionType ===
                        QUESTION_TYPE.YES_NO_MAYBE ||
                      questionData.questionType === QUESTION_TYPE.YES_NO ||
                      questionData.questionType === QUESTION_TYPE.LIKERT ||
                      questionData.questionType ===
                        QUESTION_TYPE.MULTIPLE_SELECT ||
                      questionData.questionType ===
                        QUESTION_TYPE.MULTIPLE_CHOICE
                        ? [...questionData.multiOptionResponseAttributes]
                        : null,
                    sliderResponseAttributes:
                      questionData.questionType ===
                        QUESTION_TYPE.NUMBER_SLIDER ||
                      questionData.questionType === QUESTION_TYPE.PERCENTAGE
                        ? { ...questionData.sliderResponseAttributes }
                        : null,
                    openResponseAttributes:
                      questionData.questionType === QUESTION_TYPE.COMMENTS
                        ? { ...questionData.openResponseAttributes }
                        : null,
                    //followupQuestions: []
                    questionSettings: {
                      ...questionData.questionSettings,
                      isAdmin: true,
                    },
                    audiences: audiences.reduce((a, b) => a + b, 0),
                  };
                  questionService
                    .addUpdateQuestionForSuperAdmin(data)
                    .then((res) => {
                      if (res.StatusCode && res.StatusCode !== 200) {
                        setShowOutput({
                          open: true,
                          message: 'Error while saving question',
                          type: 'error',
                        });
                        return;
                      }
                      setQuestionData(res);
                      setShowOutput({
                        open: true,
                        message: 'Question saved successfully',
                        type: 'success',
                      });
                      updateAfterEdit(res);
                    })
                    .catch((err) => {
                      console.log(err);
                    });
                }}
              >
                {questionData.id === 0 ? 'Add' : 'Update'} Question
              </AppButton>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
}
